// Plan definition + a hook exposing the current user's plan state.
//
// Four paid tiers + admin (staff-only) + beta (legacy — grandfathered
// invitees). The backend GET /profile returns the plan id; the tier
// catalog here (TIERS) is the source of truth for display metadata
// (price, storage, included frames). Backend authoritative on the
// current tier and the balance; frontend just renders.
//
// Credit pricing shape: ≤500 frames = 0 credits (drizzle or not);
// >500 frames = frames × 1 (non-drizzle) or frames × 2 (drizzle) —
// step function, not marginal. Source of truth: computeCredits() in
// services/instances.ts (mirrors lambda-start-job/ledger.go). See
// project_pricing_shape memory.

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { getProfile } from './api';

export type PlanId =
  | 'first-light'   // free tier
  | 'full-stack'    // $15/mo
  | 'tall-stack'    // $49/mo
  | 'beta'          // legacy — grandfathered invite tier
  | 'admin';        // staff — unlimited

/** True when the plan unlocks pro-tier fan-out (Split axis fan-out,
 *  Panels fan-out under a shared BatchID). Mirrors canFanOutStacks in
 *  lambda-start-job/ledger.go — backend enforces the same rule with a
 *  403 PRO_FEATURE_REQUIRED. Beta included: grandfathered founder
 *  pricing keeps early users on the paid features they helped test. */
export const canFanOutStacks = (id: string | null | undefined): boolean =>
  id === 'admin' || id === 'full-stack' || id === 'tall-stack' || id === 'beta';

const GB = 1024 * 1024 * 1024;

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  /** Monthly USD price. 0 for free / legacy / admin. */
  monthlyUSD: number;
  /** Storage quota in bytes. Infinity for admin. */
  storageBytes: number;
  /** Monthly frame allowance included in the tier. Infinity for admin. */
  monthlyFrames: number;
  /** Marketing sub-copy — used on the upgrade picker. */
  perkLine: string;
}

// Tier catalog — source of truth for display; the same metadata will
// be mirrored to Stripe products/prices (Stripe stores IDs; this table
// keeps the human-facing shape in one place).
export const FIRST_LIGHT_PLAN: Plan = {
  id: 'first-light',
  name: 'First Light',
  tagline: 'Free to try — see if the workflow fits.',
  monthlyUSD: 0,
  storageBytes: 10 * GB,
  monthlyFrames: 1000,
  perkLine: '10 GB · 1,000 frames/month · every feature',
};

export const FULL_STACK_PLAN: Plan = {
  id: 'full-stack',
  name: 'Full Stack',
  tagline: 'Serious deep-sky, mosaic sessions.',
  monthlyUSD: 15,
  // Cap sized to hit ≥1.3× worst-case margin at S3 Standard pricing.
  // Bumps back up once deep-archive lifecycle transitions ship.
  storageBytes: 300 * GB,
  monthlyFrames: 12000,
  perkLine: '300 GB · 12,000 frames/month · top-ups available',
};

export const TALL_STACK_PLAN: Plan = {
  id: 'tall-stack',
  name: 'Tall Stack',
  tagline: 'Years of raws, multiple targets in rotation.',
  monthlyUSD: 49,
  // See FULL_STACK note — cap sized around S3 Standard economics.
  // Will bump to ~2 TB when deep-archive lifecycle is wired.
  storageBytes: 1000 * GB,
  monthlyFrames: 50000,
  perkLine: '1 TB · 50,000 frames/month · priority scheduling',
};

// Grandfathered invite plan — kept in the catalog so legacy users
// don't break when the backend still returns 'beta'. New users get
// 'first-light' instead.
export const BETA_PLAN: Plan = {
  id: 'beta',
  name: 'Closed Beta',
  tagline: 'Invite-only. Founder perks locked in.',
  monthlyUSD: 0,
  storageBytes: 400 * GB,
  monthlyFrames: 5000,
  perkLine: '400 GB · 5,000 frames/month · founder-locked pricing',
};

// Admin plan is a display-only marker. Backend derives it from the `admin`
// Cognito group and skips balance + storage-quota guards (see ledger.go /
// main.go / archive.go). Meters render ∞ instead of used/quota bars.
export const ADMIN_PLAN: Plan = {
  id: 'admin',
  name: 'Admin',
  tagline: 'Unlimited storage, unlimited stacking minutes.',
  monthlyUSD: 0,
  storageBytes: Infinity,
  monthlyFrames: Infinity,
  perkLine: 'Everything, unlimited.',
};

const PLANS: Record<PlanId, Plan> = {
  'first-light': FIRST_LIGHT_PLAN,
  'full-stack':  FULL_STACK_PLAN,
  'tall-stack':  TALL_STACK_PLAN,
  beta:  BETA_PLAN,
  admin: ADMIN_PLAN,
};

// Publicly-selectable tiers in display order (ascending price). Used by
// the plan-picker section on the Profile page. Excludes beta (legacy)
// and admin (staff-only).
export const PUBLIC_TIERS: Plan[] = [
  FIRST_LIGHT_PLAN,
  FULL_STACK_PLAN,
  TALL_STACK_PLAN,
];

// Top-up packs — one-time purchases that add non-expiring credits to
// the user's balance. Independent of tier — a First Light user can buy
// a Small pack the same as a Tall Stack user. Per-credit AWS cost is
// ~$0.0002 (see project_pipeline_baselines memory), so even the Bulk
// pack retains ≥4× margin at typical workload mix.
export interface TopupPack {
  id: string;
  name: string;
  priceUSD: number;
  frames: number;
  /** Effective price per 1000 frames — for the display sub-line. */
  perThousand: number;
  tagline: string;
}

export const TOPUP_PACKS: TopupPack[] = [
  {
    id: 'small',
    name: 'Sugar',
    priceUSD: 3,
    frames: 1000,
    perThousand: 3.00,
    tagline: 'A weekend session worth of stacking.',
  },
  {
    id: 'bulk',
    name: 'Butter',
    priceUSD: 10,
    frames: 5000,
    perThousand: 2.00,
    tagline: 'A busy month of clear nights — 33% off.',
  },
  {
    id: 'whale',
    name: 'Maple',
    priceUSD: 30,
    frames: 20000,
    perThousand: 1.50,
    tagline: 'Set it and forget it — 50% off. Never expires.',
  },
];

/** Subscription state as returned by GET /profile.subscription. Null when
 *  the caller has no Stripe subscription row (admin / beta / first-light
 *  fallback tiers all render as null). */
export type SubscriptionStatus = 'active' | 'past_due' | 'cancelled';
export interface SubscriptionState {
  status: SubscriptionStatus;
  /** ISO-8601 next-invoice timestamp. Empty string until Stripe reports one. */
  renewalAt: string;
  /** ISO-8601 timestamp when a cancel-at-period-end is pending. Empty
   *  string when the subscription is not scheduled to cancel. */
  cancelAt: string;
  /** Last 4 of the card on file. Empty when not surfaced. */
  paymentMethodLast4: string;
}

export interface UserPlanState {
  loading: boolean;
  error: string | null;
  /** null when the token has no plan claim we recognize — shouldn't happen
   *  behind RequireAuth (post-confirm Lambda adds free), but the hook stays
   *  honest about it. */
  plan: Plan | null;
  /** True when the caller's plan has no storage/credits cap (currently
   *  admin only). Meter components render ∞ when this is set. */
  isUnlimited: boolean;
  creditsBalance: number;
  creditsMonthlyGrant: number;
  /** ISO-8601 timestamp — next anniv day (first-light) or paid renewal
   *  proxy. Null when the backend doesn't populate it (beta / admin, and
   *  paid tiers where subscription.renewalAt is the truth). */
  nextGrantAt: string | null;
  /** firstLightStockpileCap — set only for first-light callers. Null for
   *  beta / paid / admin (they have no cap concept). Presence is the
   *  cap-aware-copy switch across CreditsPanel / Dashboard / Profile /
   *  NewJob NEGATIVE_BALANCE error. */
  creditsCap: number | null;
  /** True iff the next anniv grant will actually issue. Null when the
   *  backend didn't send it (non-first-light). */
  willIssueNext: boolean | null;
  /** SUM(job.DebitCredits for pending+running jobs) — credits reserved
   *  by in-flight work. Always numeric (backend sends 0 when nothing is
   *  pending). Available for every tier. */
  pendingSpend: number;
  storageUsedBytes: number;
  storageWorkingBytes: number;
  storageResultsBytes: number;
  storageArchivedBytes: number;
  /** Multiplier the backend applied to archivedBytes when computing
   *  usedBytes (0.70 today). Surfaced so the UI's split bar and the
   *  archived-legend annotation agree with server math. */
  storageArchiveDiscount: number;
  storageQuotaBytes: number;
  /** Stripe subscription state, when the caller has one. Absent for
   *  admin / beta / first-light fallback tiers. */
  subscription: SubscriptionState | null;
}

const EMPTY: UserPlanState = {
  loading: true,
  error: null,
  plan: null,
  isUnlimited: false,
  creditsBalance: 0,
  creditsMonthlyGrant: 0,
  nextGrantAt: null,
  creditsCap: null,
  willIssueNext: null,
  pendingSpend: 0,
  storageUsedBytes: 0,
  storageWorkingBytes: 0,
  storageResultsBytes: 0,
  storageArchivedBytes: 0,
  storageArchiveDiscount: 1,
  storageQuotaBytes: 0,
  subscription: null,
};

/**
 * Fetches the current user's plan + usage from GET /profile. That endpoint
 * lazy-grants the monthly stacking-minutes refill on the first call each
 * UTC month, so this hook is what pulls the grant trigger from the UI side.
 *
 * Backed by TanStack Query so multiple consumers (Header, Dashboard,
 * PanalyzerModal, etc.) share ONE in-flight request per tab instead of
 * each firing their own /profile call — the pre-cache webapp doubled
 * /profile on every dashboard mount (Header + useUserPlan both fired
 * independently). Mutations that change credits/storage (startJob,
 * top-up completion, archive/unarchive) must call queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY }).
 * staleTime is 0 because the backend lazy-writes grant + debit rows
 * during the read; caching the response would mask a just-completed
 * job's debit.
 */
export const PROFILE_QUERY_KEY = ['profile'] as const;

export const useUserPlan = (): UserPlanState => {
  const { getIdToken, isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    enabled: isAuthenticated,
    staleTime: 0,
    queryFn: async () => {
      const token = await getIdToken();
      return getProfile({ token });
    },
  });

  return React.useMemo<UserPlanState>(() => {
    if (!isAuthenticated) return { ...EMPTY, loading: false };
    if (query.isPending) return EMPTY;
    if (query.isError) return { ...EMPTY, loading: false, error: (query.error as Error).message };
    const resp = query.data!;
    const plan = PLANS[resp.plan as PlanId] ?? FIRST_LIGHT_PLAN;
    const sub = resp.subscription
      ? {
          status: resp.subscription.status as SubscriptionStatus,
          renewalAt: resp.subscription.renewalAt ?? '',
          cancelAt: resp.subscription.cancelAt ?? '',
          paymentMethodLast4: resp.subscription.paymentMethodLast4 ?? '',
        }
      : null;
    return {
      loading: false,
      error: null,
      plan,
      isUnlimited: plan.id === 'admin',
      creditsBalance: resp.credits.balance,
      creditsMonthlyGrant: resp.credits.monthlyGrant,
      nextGrantAt: resp.credits.nextGrantAt ?? null,
      // Cap / willIssueNext are backend-tier-gated: first-light only.
      // Coerce absent → null so consumers can use presence as the
      // "should I render cap-aware copy?" switch without conflating
      // "field not sent" with "cap is zero".
      creditsCap: typeof resp.credits.cap === 'number' ? resp.credits.cap : null,
      willIssueNext: typeof resp.credits.willIssueNext === 'boolean' ? resp.credits.willIssueNext : null,
      pendingSpend: resp.credits.pendingSpend ?? 0,
      storageUsedBytes: resp.storage.usedBytes,
      storageWorkingBytes: resp.storage.workingBytes,
      storageResultsBytes: resp.storage.resultsBytes ?? 0,
      storageArchivedBytes: resp.storage.archivedBytes,
      storageArchiveDiscount: resp.storage.archiveDiscount,
      storageQuotaBytes: resp.storage.quotaBytes,
      subscription: sub,
    };
  }, [isAuthenticated, query.isPending, query.isError, query.error, query.data]);
};

export const formatBytes = (bytes: number): string => {
  if (bytes >= GB) return `${(bytes / GB).toFixed(bytes % GB === 0 ? 0 : 1)} GB`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1)   return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
  const kb = bytes / 1024;
  if (kb >= 1)   return `${kb.toFixed(0)} KB`;
  return `${bytes} B`;
};
